import React, { useState } from 'react';
import { Modal, Input, Button, Space, Alert } from 'antd';
import { EyeOutlined, EyeInvisibleOutlined, CopyOutlined, DownloadOutlined } from '@ant-design/icons';
import useTranslation from '@hooks/useTranslation';
import organizationApi from '@src/api/organization/organizationApi';
import styles from './ApiKeys.module.css';
import StyledButton from '@components/form-control/styledButton/styledButton';
import useGlobalNotification from '@hooks/useGlobalNotification';

type Props = {
  visible: boolean;
  onClose: () => void;
};

/**
 * Modal component for generating a new API key
 */
const GenerateApiKeyModal: React.FC<Props> = ({ visible, onClose }) => {
  const t = useTranslation('AccountMenu.Modal.ApiKeys.GenerateModal');
  const tCommon = t.withBaseKey('CommonWords');
  const { showNotification } = useGlobalNotification();
  
  const [comment, setComment] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [keyId, setKeyId] = useState<string | null>(null);
  const [masked, setMasked] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Handles generating a new API key
   */
  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const response = await organizationApi.generateApiKey(comment || undefined);
      setGeneratedKey(response.apiKey);
      setKeyId(response.id);
    } catch (err) {
      console.error('Failed to generate API key:', err);
      setError(err instanceof Error ? err.message : t('Error.GenerateFailed'));
      showNotification({
        type: 'error',
        title: tCommon('Error'),
        message: t('Error.GenerateFailed'),
      });
    } finally {
      setGenerating(false);
    }
  };

  /**
   * Handles copying the API key to clipboard
   */
  const handleCopy = async () => {
    if (!generatedKey) return;
    
    try {
      await navigator.clipboard.writeText(generatedKey);
      showNotification({
        type: 'success',
        title: tCommon('Success'),
        message: t('Success.CopiedToClipboard'),
      });
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      showNotification({
        type: 'error',
        title: tCommon('Error'),
        message: t('Error.CopyFailed'),
      });
    }
  };

  /**
   * Handles downloading the API key as a text file
   */
  const handleDownload = () => {
    if (!generatedKey) return;
    
    const content = `KEMU_API_KEY=${generatedKey}`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'kemu-api-key.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    showNotification({
      type: 'success',
      title: tCommon('Success'),
      message: t('Success.Downloaded'),
    });
  };

  /**
   * Handles modal close and resets state
   */
  const handleClose = () => {
    setComment('');
    setGeneratedKey(null);
    setKeyId(null);
    setMasked(true);
    setError(null);
    onClose();
  };

  /**
   * Toggles the mask/unmask state of the API key
   */
  const toggleMask = () => {
    setMasked(!masked);
  };

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={null}
      title={t('Title')}
      width={600}
      destroyOnClose
    >
      {error && (
        <Alert
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          className={styles.ErrorAlert}
          style={{ marginBottom: 16 }}
        />
      )}
      
      {!generatedKey ? (
        <div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8 }}>
              {t('CommentLabel')} ({tCommon('Optional')})
            </label>
            <Input
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('CommentPlaceholder')}
              maxLength={255}
            />
          </div>
          <div style={{ textAlign: 'right', marginTop: 24 }}>
            <Space>
              <StyledButton
                noShadow
                color="light"
                title={tCommon('Cancel')}
                onClick={handleClose}
              />
              <StyledButton
                noShadow
                color="primary"
                title={t('GenerateButton')}
                onClick={handleGenerate}
                loading={generating}
              />
            </Space>
          </div>
        </div>
      ) : (
        <div>
          <Alert
            message={t('Warning.Title')}
            description={t('Warning.Description')}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8 }}>
              {t('ApiKeyLabel')}
            </label>
            <Space.Compact style={{ display: 'flex', width: '100%' }}>
              <Input
                value={generatedKey}
                readOnly
                type={masked ? 'password' : 'text'}
                style={{ flex: 1 }}
                className={styles.ApiKeyInput}
              />
              <Button
                icon={masked ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                onClick={toggleMask}
                title={masked ? t('Actions.Show') : t('Actions.Hide')}
              />
              <Button
                icon={<CopyOutlined />}
                onClick={handleCopy}
                title={tCommon('Copy')}
              />
              <Button
                icon={<DownloadOutlined />}
                onClick={handleDownload}
                title={tCommon('Download')}
              />
            </Space.Compact>
          </div>
          <div style={{ textAlign: 'right', marginTop: 24 }}>
            <StyledButton
              noShadow
              color="primary"
              title={tCommon('Close')}
              onClick={handleClose}
            />
          </div>
        </div>
      )}
    </Modal>
  );
};

export default GenerateApiKeyModal;

